import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Flex, Heading, Text, Badge, Table, Thead, Tbody, Tr, Th, Td,
  Button, Container, Stat, StatLabel, StatNumber, StatGroup, StatHelpText, StatArrow,
  useToast, SimpleGrid, Card, CardBody, Stack, useBreakpointValue, Divider,
  Input, InputGroup, InputLeftElement, Spinner, Select, Tooltip, Icon, Collapse, Link,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  VStack, HStack, Tabs, TabList, Tab
} from "@chakra-ui/react";
import { ArrowBackIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, RepeatIcon, CheckIcon, WarningIcon, TimeIcon, InfoIcon } from "@chakra-ui/icons";
import { MdAttachMoney, MdTrendingDown, MdShoppingCart, MdInventory, MdDiscount, MdReceipt, MdFileDownload } from "react-icons/md";
import { FaExternalLinkAlt } from "react-icons/fa";
import { api } from "../services/api";
import { getIntegracoes } from "../services/api";
import Preloader from "../components/Preloader";
import ProdutoImagem from "../components/ProdutoImagem";

const DetalheListaPromocao = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [lista, setLista] = useState(null);
  const [loading, setLoading] = useState(true);

  const [itens, setItens] = useState([]);
  const [itensPagination, setItensPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [itensLoading, setItensLoading] = useState(false);
  const [buscaItens, setBuscaItens] = useState("");
  const [perPage, setPerPage] = useState(50);

  const [vendas, setVendas] = useState([]);
  const [vendasPagination, setVendasPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [vendasLoading, setVendasLoading] = useState(false);
  const [metricas, setMetricas] = useState(null);
  const [vendasExpandidas, setVendasExpandidas] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [buscaVendas, setBuscaVendas] = useState("");
  const [integracoes, setIntegracoes] = useState({});

  const [syncFilter, setSyncFilter] = useState('todos');
  const [syncModal, setSyncModal] = useState({ open: false, item: null });
  const [syncModalData, setSyncModalData] = useState([]);
  const [syncModalLoading, setSyncModalLoading] = useState(false);
  const [forceSyncLoading, setForceSyncLoading] = useState(false);

  const isMobile = useBreakpointValue({ base: true, lg: false });

  useEffect(() => {
    const fetchDetalhes = async () => {
      try {
        const response = await api.get(`/lista-descontos/${id}`);
        setLista(response.data);
      } catch (error) {
        toast({ title: "Erro", description: "Não foi possível carregar a lista.", status: "error" });
        navigate("/promocoes");
      } finally {
        setLoading(false);
      }
    };
    fetchDetalhes();
  }, [id, navigate, toast]);

  const fetchItens = useCallback(async (page = 1) => {
    setItensLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (buscaItens) params.busca = buscaItens;
      if (syncFilter && syncFilter !== 'todos') params.sync_filter = syncFilter;
      const response = await api.get(`/lista-descontos/${id}/itens`, { params });
      const itensData = response.data.data;
      setItens(itensData);
      setItensPagination({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        total: response.data.total,
      });

      // Buscar integrações ML para os itens carregados
      const estoqueIds = itensData.map(i => i.estoque?.id).filter(Boolean);
      if (estoqueIds.length > 0) {
        const integracoesData = await getIntegracoes({ ids: estoqueIds });
        setIntegracoes(prev => ({ ...prev, ...integracoesData }));
      }
    } catch (error) {
      toast({ title: "Erro ao carregar itens", status: "error" });
    } finally {
      setItensLoading(false);
    }
  }, [id, buscaItens, perPage, syncFilter, toast]);

  const fetchVendas = useCallback(async (page = 1) => {
    setVendasLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (buscaVendas) params.busca = buscaVendas;
      const response = await api.get(`/lista-descontos/${id}/vendas`, { params });
      setVendas(response.data.vendas.data);
      setVendasPagination({
        current_page: response.data.vendas.current_page,
        last_page: response.data.vendas.last_page,
        total: response.data.vendas.total,
      });
      setMetricas(response.data.metricas);
    } catch (error) {
      toast({ title: "Erro ao carregar vendas", status: "error" });
    } finally {
      setVendasLoading(false);
    }
  }, [id, buscaVendas, toast]);

  const handleExportarCSV = async () => {
    setExportLoading(true);
    try {
      const response = await api.get(`/lista-descontos/${id}/vendas/exportar`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resultado_promocao_${id}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Erro ao exportar", description: "Não foi possível gerar o CSV.", status: "error" });
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) fetchItens(1);
  }, [loading, fetchItens]);

  useEffect(() => {
    if (!loading) fetchVendas(1);
  }, [loading, fetchVendas]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (!loading) fetchItens(1);
    }, 400);
    return () => clearTimeout(delay);
  }, [buscaItens]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (!loading) fetchVendas(1);
    }, 400);
    return () => clearTimeout(delay);
  }, [buscaVendas]);

  const fetchSyncStatus = async (item) => {
    setSyncModalLoading(true);
    setSyncModalData([]);
    try {
      const response = await api.get(`/lista-descontos/${id}/itens/${item.produto_id}/sync-status`);
      setSyncModalData(response.data);
    } catch (e) {
      toast({ title: "Erro ao carregar status de sync", status: "error", duration: 3000 });
    } finally {
      setSyncModalLoading(false);
    }
  };

  const handleForceSync = async () => {
    if (!syncModal.item) return;
    setForceSyncLoading(true);
    try {
      const response = await api.post(`/lista-descontos/${id}/itens/${syncModal.item.produto_id}/force-sync`);
      setSyncModalData(response.data.sync_status || []);
      const res = response.data.resultado;
      toast({
        title: res?.sucesso ? "Sync concluído com sucesso" : "Sync finalizado",
        description: res?.msg || "Processado",
        status: res?.sucesso ? "success" : "warning",
        duration: 4000,
      });
      fetchItens(itensPagination.current_page);
    } catch (e) {
      toast({
        title: "Erro ao forçar sync",
        description: e?.response?.data?.msg || "Erro desconhecido",
        status: "error",
        duration: 4000,
      });
    } finally {
      setForceSyncLoading(false);
    }
  };

  const openSyncModal = (item) => {
    setSyncModal({ open: true, item });
    fetchSyncStatus(item);
  };

  if (loading) {
    return <Flex justify="center" align="center" h="100vh"><Preloader /></Flex>;
  }

  if (!lista) return null;

  const getOrigemLabel = (val) => {
    const map = {
        '0': 'TAGA', '1': 'MORELATE', '2': 'VANNUCCI', '3': 'ISAPA',
        '4': 'BRCOMPANY', '5': 'AUTOLATINA', '6': 'LNG', '7': 'CBA', '8': 'MSAM', '9': 'BLPARTS', '100': 'OUTROS'
    };
    return map[val] || val;
  }

  const syncStatusConfig = {
    synced:      { color: 'green',  label: 'Sincronizado', icon: CheckIcon },
    error:       { color: 'red',    label: 'Erro',         icon: WarningIcon },
    skip:        { color: 'yellow', label: 'Skip',         icon: TimeIcon },
    sem_anuncio: { color: 'gray',   label: 'Sem anúncio',  icon: InfoIcon },
    pendente:    { color: 'blue',   label: 'Pendente',     icon: TimeIcon },
  };

  const SyncBadge = ({ status, onClick }) => {
    const cfg = syncStatusConfig[status] || syncStatusConfig.pendente;
    return (
      <Tooltip label="Ver status de sync" hasArrow>
        <Badge
          colorScheme={cfg.color}
          cursor="pointer"
          onClick={onClick}
          display="inline-flex"
          alignItems="center"
          gap={1}
          px={2}
          py={1}
          borderRadius="md"
          fontSize="2xs"
          _hover={{ opacity: 0.8 }}
        >
          <Icon as={cfg.icon} boxSize="10px" />
          {cfg.label}
        </Badge>
      </Tooltip>
    );
  };

  const renderSyncModal = () => {
    const item = syncModal.item;
    return (
      <Modal isOpen={syncModal.open} onClose={() => setSyncModal({ open: false, item: null })} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={2}>
            <Text fontSize="md" fontWeight="bold">Status de Sincronização</Text>
            {item && (
              <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
                {item.produto?.nome} — SKU {item.produto_id}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {syncModalLoading ? (
              <Flex justify="center" py={8}><Spinner size="lg" color="blue.400" /></Flex>
            ) : syncModalData.length === 0 ? (
              <Flex direction="column" align="center" py={8} color="gray.400">
                <Icon as={TimeIcon} boxSize={8} mb={2} />
                <Text fontSize="sm">Nenhum registro de sync encontrado.</Text>
                <Text fontSize="xs" mt={1}>Use "Forçar Sync" para processar este item.</Text>
              </Flex>
            ) : (
              <VStack spacing={3} align="stretch">
                {syncModalData.map((row, idx) => {
                  const cfg = syncStatusConfig[row.status] || syncStatusConfig.pendente;
                  return (
                    <Box key={idx} border="1px solid" borderColor={`${cfg.color}.200`} bg={`${cfg.color}.50`} borderRadius="md" p={3}>
                      <Flex justify="space-between" align="center" mb={2}>
                        <HStack spacing={2}>
                          <Badge colorScheme="purple" fontSize="xs">
                            Conta {row.conta_id || '?'}
                          </Badge>
                          {row.anuncio_id && (
                            <Text fontSize="2xs" color="gray.600" fontFamily="mono">{row.anuncio_id}</Text>
                          )}
                        </HStack>
                        <Badge colorScheme={cfg.color} fontSize="xs" display="flex" alignItems="center" gap={1}>
                          <Icon as={cfg.icon} boxSize="10px" />
                          {cfg.label}
                        </Badge>
                      </Flex>

                      <SimpleGrid columns={2} spacing={2} fontSize="xs">
                        <Box>
                          <Text color="gray.500" fontSize="2xs" textTransform="uppercase" fontWeight="bold">Última tentativa</Text>
                          <Text>{row.ultima_tentativa_at ? new Date(row.ultima_tentativa_at).toLocaleString('pt-BR') : '-'}</Text>
                        </Box>
                        <Box>
                          <Text color="gray.500" fontSize="2xs" textTransform="uppercase" fontWeight="bold">Offer ID</Text>
                          <Text fontFamily="mono" fontSize="2xs" isTruncated>{row.offer_id || '-'}</Text>
                        </Box>
                        <Box>
                          <Text color="gray.500" fontSize="2xs" textTransform="uppercase" fontWeight="bold">Preço alvo</Text>
                          <Text fontWeight="bold" color="blue.700">
                            {row.preco_alvo ? `R$ ${parseFloat(row.preco_alvo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                          </Text>
                        </Box>
                        <Box>
                          <Text color="gray.500" fontSize="2xs" textTransform="uppercase" fontWeight="bold">Preço aplicado</Text>
                          <Text fontWeight="bold" color={row.preco_aplicado ? "green.700" : "gray.400"}>
                            {row.preco_aplicado ? `R$ ${parseFloat(row.preco_aplicado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                          </Text>
                        </Box>
                      </SimpleGrid>

                      {row.ultimo_erro && (
                        <Box mt={2} p={2} bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md">
                          <Text fontSize="2xs" fontWeight="bold" color="red.600" mb={1}>ÚLTIMO ERRO</Text>
                          <Text fontSize="xs" color="red.700" wordBreak="break-word">{row.ultimo_erro}</Text>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
            <Button
              colorScheme="blue"
              size="sm"
              leftIcon={<RepeatIcon />}
              onClick={handleForceSync}
              isLoading={forceSyncLoading}
              loadingText="Sincronizando..."
            >
              Forçar Sync
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSyncModal({ open: false, item: null })}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  const renderPagination = () => (
    <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={3}>
      <Flex align="center" gap={2}>
        <Text fontSize="sm" color="gray.600">
          {itensPagination.total} itens | Página {itensPagination.current_page} de {itensPagination.last_page}
        </Text>
        <Select size="sm" w="80px" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </Select>
      </Flex>
      <Flex gap={2}>
        <Button
          size="sm"
          leftIcon={<ChevronLeftIcon />}
          onClick={() => fetchItens(itensPagination.current_page - 1)}
          isDisabled={itensPagination.current_page <= 1}
        >
          Anterior
        </Button>
        <Button
          size="sm"
          rightIcon={<ChevronRightIcon />}
          onClick={() => fetchItens(itensPagination.current_page + 1)}
          isDisabled={itensPagination.current_page >= itensPagination.last_page}
        >
          Próxima
        </Button>
      </Flex>
    </Flex>
  );

  const renderTable = () => (
    <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.200">
        <Table variant="simple" size="sm" bg="white">
            <Thead bg="gray.100">
                <Tr>
                    <Th>Produto</Th>
                    <Th>Cód. Forn.</Th>
                    <Th textAlign="center">Anúncios ML</Th>
                    <Th textAlign="center">Sync ML</Th>
                    <Th isNumeric bg="orange.50" color="orange.800" borderBottom="2px solid" borderColor="orange.200">Custo Promo</Th>
                    <Th isNumeric bg="orange.50" color="orange.800" borderBottom="2px solid" borderColor="orange.200">Desc. Custo</Th>
                    <Th isNumeric bg="green.50" color="green.800" borderBottom="2px solid" borderColor="green.200">Venda Promo</Th>
                    <Th isNumeric bg="green.50" color="green.800" borderBottom="2px solid" borderColor="green.200">Desc. Venda</Th>
                </Tr>
            </Thead>
            <Tbody>
                {itens.map((item) => {
                    const produtoParaImagem = {
                        ...item.produto,
                        codigo_fornecedor: item.codigo_fornecedor,
                        fornecedor_id: item.fornecedor_id ? parseInt(item.fornecedor_id, 10) : null
                    };

                    return (
                        <Tr key={item.id} _hover={{ bg: "gray.50" }}>
                            <Td>
                                <Flex align="center" gap={3}>
                                    <Box boxSize="40px" minW="40px" borderRadius="md" overflow="hidden" border="1px solid #eee">
                                        <ProdutoImagem produto={produtoParaImagem} width="100%" height="100%" />
                                    </Box>
                                    <Box>
                                        <Flex align="center" gap={1}>
                                            <Text fontWeight="bold" fontSize="xs" noOfLines={2} title={item.produto?.nome}>{item.produto?.nome}</Text>
                                            <Link href={`/produtos?id=${item.produto_id}`} isExternal mt="0.5">
                                                <FaExternalLinkAlt size={10} color="#3182ce" />
                                            </Link>
                                        </Flex>
                                        <Text fontSize="2xs" color="gray.500">SKU: {item.produto_id} | Global: {item.produto?.codigo_global}</Text>
                                    </Box>
                                </Flex>
                            </Td>
                            <Td fontSize="xs">{item.codigo_fornecedor || "-"}</Td>
                            <Td>
                                {(() => {
                                    const info = integracoes[item.estoque?.id];
                                    if (!info) return <Text fontSize="2xs" color="gray.400">-</Text>;
                                    const mlUrl = (idMl) => `https://produto.mercadolivre.com.br/MLB-${idMl.replace("MLB", "")}`;
                                    return (
                                        <Flex gap={1} justify="center">
                                            {info.id_ml_conta_1 && (
                                                <Tooltip label={`Conta 1: ${info.id_ml_conta_1}`} hasArrow placement="top">
                                                    <Button
                                                        as="a"
                                                        href={mlUrl(info.id_ml_conta_1)}
                                                        target="_blank"
                                                        size="xs"
                                                        h="22px"
                                                        px={1}
                                                        bg="#FEE600"
                                                        borderWidth="1px"
                                                        borderColor="black"
                                                        borderRadius="md"
                                                        _hover={{ transform: 'scale(1.08)' }}
                                                        transition="transform 0.15s ease-out"
                                                    >
                                                        <Text fontSize="2xs" fontWeight="bold">ML1</Text>
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {info.id_ml_conta_2 && (
                                                <Tooltip label={`Conta 2: ${info.id_ml_conta_2}`} hasArrow placement="top">
                                                    <Button
                                                        as="a"
                                                        href={mlUrl(info.id_ml_conta_2)}
                                                        target="_blank"
                                                        size="xs"
                                                        h="22px"
                                                        px={1}
                                                        bg="#FEE600"
                                                        borderWidth="2px"
                                                        borderColor="blue.500"
                                                        borderRadius="md"
                                                        _hover={{ transform: 'scale(1.08)' }}
                                                        transition="transform 0.15s ease-out"
                                                    >
                                                        <Text fontSize="2xs" fontWeight="bold">ML2</Text>
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {info.id_ml_conta_3 && (
                                                <Tooltip label={`Conta 3: ${info.id_ml_conta_3}`} hasArrow placement="top">
                                                    <Button
                                                        as="a"
                                                        href={mlUrl(info.id_ml_conta_3)}
                                                        target="_blank"
                                                        size="xs"
                                                        h="22px"
                                                        px={1}
                                                        bg="#FEE600"
                                                        borderWidth="2px"
                                                        borderColor="green.500"
                                                        borderRadius="md"
                                                        _hover={{ transform: 'scale(1.08)' }}
                                                        transition="transform 0.15s ease-out"
                                                    >
                                                        <Text fontSize="2xs" fontWeight="bold">ML3</Text>
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Flex>
                                    );
                                })()}
                            </Td>
                            <Td textAlign="center">
                                <SyncBadge status={item.sync_status_summary} onClick={() => openSyncModal(item)} />
                            </Td>

                            <Td isNumeric bg="orange.50">
                                <Text fontSize="2xs" color="gray.500" textDecoration="line-through">R$ {item.preco_compra_original}</Text>
                                <Text fontWeight="bold" fontSize="sm" color="orange.700">
                                    {item.preco_compra_promocional ? `R$ ${item.preco_compra_promocional}` : "-"}
                                </Text>
                            </Td>
                            <Td isNumeric bg="orange.50" color="orange.600" fontWeight="bold" fontSize="xs">
                                {item.porcentagem_desconto_preco_compra > 0 ? `${parseFloat(item.porcentagem_desconto_preco_compra).toFixed(2)}%` : "-"}
                            </Td>

                            <Td isNumeric bg="green.50">
                                <Text fontSize="2xs" color="gray.500" textDecoration="line-through">R$ {item.preco_venda_original}</Text>
                                <Text fontWeight="bold" fontSize="sm" color="green.700">R$ {item.preco_venda_promocional}</Text>
                            </Td>
                            <Td isNumeric bg="green.50" color="green.600" fontWeight="bold" fontSize="xs">
                                {parseFloat(item.porcentagem_desconto_preco_venda).toFixed(2)}%
                            </Td>
                        </Tr>
                    );
                })}
            </Tbody>
        </Table>
    </Box>
  );

  const renderCards = () => (
      <SimpleGrid columns={1} spacing={4}>
          {itens.map((item) => {
              const produtoParaImagem = {
                  ...item.produto,
                  codigo_fornecedor: item.codigo_fornecedor,
                  fornecedor_id: item.fornecedor_id ? parseInt(item.fornecedor_id, 10) : null
              };

              return (
                  <Card key={item.id} shadow="sm" border="1px solid" borderColor="gray.200">
                      <CardBody p={4}>
                          <Flex gap={3} mb={3}>
                              <Box boxSize="50px" minW="50px" borderRadius="md" overflow="hidden" border="1px solid #eee">
                                  <ProdutoImagem produto={produtoParaImagem} width="100%" height="100%" />
                              </Box>
                              <Box flex="1" minW={0}>
                                  <Flex align="center" gap={1}>
                                      <Text fontWeight="bold" fontSize="sm" noOfLines={2}>{item.produto?.nome}</Text>
                                      <Link href={`/produtos?id=${item.produto_id}`} isExternal mt="0.5">
                                          <FaExternalLinkAlt size={10} color="#3182ce" />
                                      </Link>
                                  </Flex>
                                  <Text fontSize="xs" color="gray.500">SKU: {item.produto_id} | {item.produto?.codigo_global}</Text>
                                  {item.codigo_fornecedor && (
                                      <Text fontSize="2xs" color="gray.500">Cod. Forn.: {item.codigo_fornecedor}</Text>
                                  )}
                              </Box>
                          </Flex>

                          {(() => {
                              const info = integracoes[item.estoque?.id];
                              if (!info || (!info.id_ml_conta_1 && !info.id_ml_conta_2 && !info.id_ml_conta_3)) return null;
                              const mlUrl = (idMl) => `https://produto.mercadolivre.com.br/MLB-${idMl.replace("MLB", "")}`;
                              return (
                                  <Flex gap={2} mb={3} align="center" wrap="wrap">
                                      <Text fontSize="2xs" color="gray.500" fontWeight="bold" textTransform="uppercase">Anuncios ML:</Text>
                                      {info.id_ml_conta_1 && (
                                          <Tooltip label={`Conta 1: ${info.id_ml_conta_1}`} hasArrow placement="top">
                                              <Button
                                                  as="a"
                                                  href={mlUrl(info.id_ml_conta_1)}
                                                  target="_blank"
                                                  size="xs"
                                                  h="24px"
                                                  px={2}
                                                  bg="#FEE600"
                                                  borderWidth="1px"
                                                  borderColor="black"
                                                  borderRadius="md"
                                                  _hover={{ transform: 'scale(1.05)' }}
                                                  transition="transform 0.15s ease-out"
                                              >
                                                  <Text fontSize="2xs" fontWeight="bold">ML1</Text>
                                              </Button>
                                          </Tooltip>
                                      )}
                                      {info.id_ml_conta_2 && (
                                          <Tooltip label={`Conta 2: ${info.id_ml_conta_2}`} hasArrow placement="top">
                                              <Button
                                                  as="a"
                                                  href={mlUrl(info.id_ml_conta_2)}
                                                  target="_blank"
                                                  size="xs"
                                                  h="24px"
                                                  px={2}
                                                  bg="#FEE600"
                                                  borderWidth="2px"
                                                  borderColor="blue.500"
                                                  borderRadius="md"
                                                  _hover={{ transform: 'scale(1.05)' }}
                                                  transition="transform 0.15s ease-out"
                                              >
                                                  <Text fontSize="2xs" fontWeight="bold">ML2</Text>
                                              </Button>
                                          </Tooltip>
                                      )}
                                      {info.id_ml_conta_3 && (
                                          <Tooltip label={`Conta 3: ${info.id_ml_conta_3}`} hasArrow placement="top">
                                              <Button
                                                  as="a"
                                                  href={mlUrl(info.id_ml_conta_3)}
                                                  target="_blank"
                                                  size="xs"
                                                  h="24px"
                                                  px={2}
                                                  bg="#FEE600"
                                                  borderWidth="2px"
                                                  borderColor="green.500"
                                                  borderRadius="md"
                                                  _hover={{ transform: 'scale(1.05)' }}
                                                  transition="transform 0.15s ease-out"
                                              >
                                                  <Text fontSize="2xs" fontWeight="bold">ML3</Text>
                                              </Button>
                                          </Tooltip>
                                      )}
                                  </Flex>
                              );
                          })()}

                          <Flex align="center" justify="space-between" mb={3}>
                              <SyncBadge status={item.sync_status_summary} onClick={() => openSyncModal(item)} />
                              {item.sync_ultima_tentativa && (
                                  <Text fontSize="2xs" color="gray.400">
                                      {new Date(item.sync_ultima_tentativa).toLocaleString('pt-BR')}
                                  </Text>
                              )}
                          </Flex>

                          <Divider mb={3} />

                          <SimpleGrid columns={2} spacing={4}>
                              <Box bg="orange.50" p={2} borderRadius="md">
                                  <Text fontSize="xs" color="orange.800" fontWeight="bold" mb={1}>COMPRA</Text>
                                  <Flex justify="space-between" align="end">
                                      <Box>
                                          <Text fontSize="2xs" color="gray.500">Original</Text>
                                          <Text fontSize="xs"><s>R$ {item.preco_compra_original}</s></Text>
                                      </Box>
                                      <Box textAlign="right">
                                          <Text fontSize="2xs" color="gray.500">Promo</Text>
                                          <Text fontSize="sm" fontWeight="bold" color="orange.700">
                                              {item.preco_compra_promocional ? `R$ ${item.preco_compra_promocional}` : "-"}
                                          </Text>
                                      </Box>
                                  </Flex>
                              </Box>

                              <Box bg="green.50" p={2} borderRadius="md">
                                  <Text fontSize="xs" color="green.800" fontWeight="bold" mb={1}>VENDA</Text>
                                  <Flex justify="space-between" align="end">
                                      <Box>
                                          <Text fontSize="2xs" color="gray.500">Original</Text>
                                          <Text fontSize="xs"><s>R$ {item.preco_venda_original}</s></Text>
                                      </Box>
                                      <Box textAlign="right">
                                          <Text fontSize="2xs" color="gray.500">Promo</Text>
                                          <Text fontSize="sm" fontWeight="bold" color="green.700">R$ {item.preco_venda_promocional}</Text>
                                      </Box>
                                  </Flex>
                              </Box>
                          </SimpleGrid>
                      </CardBody>
                  </Card>
              );
          })}
      </SimpleGrid>
  );

  return (
    <Box bg="gray.50" minH="100vh" py={6}>
      <Container maxW="container.xl">
        <Button leftIcon={<ArrowBackIcon />} variant="link" mb={4} onClick={() => navigate("/promocoes")}>
          Voltar
        </Button>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" mb={6}>
          <Flex justify="space-between" align="start" direction={{ base: "column", md: "row" }} gap={4}>
            <Box>
              <Flex align="center" gap={3} mb={2}>
                  <Heading size="lg" color="gray.700">#{lista.id} - {lista.titulo}</Heading>
                  <Badge fontSize="md" colorScheme={lista.status === 'ativa' ? 'green' : 'gray'}>
                    {lista.status.toUpperCase()}
                  </Badge>
              </Flex>
              <Text color="gray.500">{lista.descricao || "Sem descrição"}</Text>
            </Box>
          </Flex>

          <Divider my={6} />

          <StatGroup textAlign="center" flexDirection={{ base: "column", sm: "row" }} gap={4}>
            <Stat>
              <StatLabel color="gray.500">Origem</StatLabel>
              <StatNumber fontSize="xl">{getOrigemLabel(lista.origem)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.500">Itens</StatLabel>
              <StatNumber fontSize="xl">{lista.quantidade_itens}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.500">Início</StatLabel>
              <StatNumber fontSize="xl">{new Date(lista.data_inicio).toLocaleDateString()}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.500">Fim</StatLabel>
              <StatNumber fontSize="xl">{lista.data_fim ? new Date(lista.data_fim).toLocaleDateString() : "-"}</StatNumber>
            </Stat>
          </StatGroup>
        </Box>

        {/* Métricas da Promoção */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md" color="gray.700">Resultado da Promoção</Heading>
            {metricas && metricas.total_vendas > 0 && (
              <Button
                size="sm"
                colorScheme="green"
                variant="outline"
                leftIcon={<MdFileDownload />}
                onClick={handleExportarCSV}
                isLoading={exportLoading}
                loadingText="Exportando..."
              >
                Exportar CSV
              </Button>
            )}
          </Flex>

          {vendasLoading && !metricas ? (
            <Flex justify="center" py={10}><Spinner size="lg" color="green.400" /></Flex>
          ) : metricas ? (
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Card bg="blue.50" border="1px solid" borderColor="blue.200">
                <CardBody p={4} textAlign="center">
                  <Icon as={MdShoppingCart} boxSize={6} color="blue.500" mb={1} />
                  <Text fontSize="xs" color="blue.700" fontWeight="bold">Vendas</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.800">{metricas.total_vendas}</Text>
                </CardBody>
              </Card>
              <Card bg="purple.50" border="1px solid" borderColor="purple.200">
                <CardBody p={4} textAlign="center">
                  <Icon as={MdInventory} boxSize={6} color="purple.500" mb={1} />
                  <Text fontSize="xs" color="purple.700" fontWeight="bold">Itens Vendidos</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.800">{metricas.total_itens_vendidos}</Text>
                  <Text fontSize="2xs" color="purple.500">{metricas.produtos_distintos} produtos distintos</Text>
                </CardBody>
              </Card>
              <Card bg="green.50" border="1px solid" borderColor="green.200">
                <CardBody p={4} textAlign="center">
                  <Icon as={MdAttachMoney} boxSize={6} color="green.500" mb={1} />
                  <Text fontSize="xs" color="green.700" fontWeight="bold">Faturamento</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.800">
                    R$ {metricas.faturamento_promocional.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Text>
                  <Text fontSize="2xs" color="green.500">
                    Ticket médio: R$ {metricas.ticket_medio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Text>
                </CardBody>
              </Card>
              <Card bg="orange.50" border="1px solid" borderColor="orange.200">
                <CardBody p={4} textAlign="center">
                  <Icon as={MdTrendingDown} boxSize={6} color="orange.500" mb={1} />
                  <Text fontSize="xs" color="orange.700" fontWeight="bold">Desconto Concedido</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.800">
                    R$ {metricas.economia_cliente.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Text>
                  <Text fontSize="2xs" color="orange.500">
                    Desconto médio: {metricas.desconto_medio_percentual.toFixed(1)}%
                  </Text>
                </CardBody>
              </Card>
            </SimpleGrid>
          ) : (
            <Text textAlign="center" py={6} color="gray.500">Nenhuma venda registrada com esta promoção.</Text>
          )}
        </Box>

        {/* Listagem de Vendas */}
        {metricas && metricas.total_vendas > 0 && (
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" mt={6}>
            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
              <Flex align="center" gap={3}>
                <Heading size="md" color="gray.700">Vendas com esta Promoção</Heading>
                <Text fontSize="sm" color="gray.500">{vendasPagination.total} vendas</Text>
              </Flex>
              <InputGroup maxW="350px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por ID, id_origem, pack, produto..."
                  value={buscaVendas}
                  onChange={(e) => setBuscaVendas(e.target.value)}
                  bg="gray.50"
                  size="sm"
                />
              </InputGroup>
            </Flex>

            {vendasLoading ? (
              <Flex justify="center" py={10}><Spinner size="lg" color="green.400" /></Flex>
            ) : (
              <>
                <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor="gray.200">
                  <Table variant="simple" size="sm" bg="white">
                    <Thead bg="gray.100">
                      <Tr>
                        <Th w="30px"></Th>
                        <Th>Venda</Th>
                        <Th>Data</Th>
                        <Th>Cliente</Th>
                        <Th>Origem</Th>
                        <Th>Status</Th>
                        <Th isNumeric>Itens Promo</Th>
                        <Th isNumeric>Valor Promo</Th>
                        <Th isNumeric>Valor Original</Th>
                        <Th isNumeric>Economia</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {vendas.map((venda) => {
                        const itensPromo = venda.itens_venda || [];
                        const totalPromo = itensPromo.reduce((acc, item) => acc + (item.valor_unitario * item.quantidade), 0);
                        const totalOriginal = itensPromo.reduce((acc, item) => acc + ((item.preco_original || item.valor_unitario) * item.quantidade), 0);
                        const economia = totalOriginal - totalPromo;
                        const qtdItens = itensPromo.reduce((acc, item) => acc + item.quantidade, 0);
                        const isExpandida = vendasExpandidas[venda.id];

                        return (
                          <React.Fragment key={venda.id}>
                            <Tr
                              _hover={{ bg: "gray.50" }}
                              cursor="pointer"
                              onClick={() => setVendasExpandidas(prev => ({ ...prev, [venda.id]: !prev[venda.id] }))}
                            >
                              <Td px={1}>
                                {isExpandida ? <ChevronUpIcon /> : <ChevronDownIcon />}
                              </Td>
                              <Td>
                                <Text fontWeight="bold" fontSize="sm" color="blue.600">#{venda.id}</Text>
                                {venda.id_origem && (
                                  <Text fontSize="2xs" color="gray.500">{venda.id_origem}</Text>
                                )}
                              </Td>
                              <Td fontSize="xs">
                                {new Date(venda.created_at).toLocaleDateString('pt-BR')}
                              </Td>
                              <Td fontSize="xs" maxW="180px">
                                <Text isTruncated>{venda.cliente?.nome || venda.cliente?.nickname || "-"}</Text>
                              </Td>
                              <Td>
                                <Badge fontSize="2xs" colorScheme={venda.origem === 'ML' ? 'yellow' : 'blue'}>
                                  {venda.origem || "TAGA"}
                                </Badge>
                              </Td>
                              <Td>
                                <Badge fontSize="2xs" colorScheme={
                                  venda.status === 'paid' || venda.status === 'Pago' ? 'green' :
                                  venda.status === 'cancelled' ? 'red' : 'gray'
                                }>
                                  {venda.status}
                                </Badge>
                              </Td>
                              <Td isNumeric fontSize="sm" fontWeight="bold">{qtdItens}</Td>
                              <Td isNumeric fontSize="sm" fontWeight="bold" color="green.600">
                                R$ {totalPromo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </Td>
                              <Td isNumeric fontSize="xs" color="gray.500" textDecoration="line-through">
                                R$ {totalOriginal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </Td>
                              <Td isNumeric fontSize="xs" fontWeight="bold" color="orange.600">
                                {economia > 0 ? `-R$ ${economia.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : "-"}
                              </Td>
                            </Tr>
                            {isExpandida && (
                              <Tr>
                                <Td colSpan={10} p={0} bg="gray.50" borderBottom="2px solid" borderColor="gray.200">
                                  <Box p={3}>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Produtos desta venda na promoção:</Text>
                                    <Table size="xs" variant="unstyled">
                                      <Thead>
                                        <Tr>
                                          <Th fontSize="2xs" py={1}>Produto</Th>
                                          <Th fontSize="2xs" py={1}>SKU</Th>
                                          <Th fontSize="2xs" py={1}>Cód. Global</Th>
                                          <Th fontSize="2xs" py={1}>Cód. Forn.</Th>
                                          <Th fontSize="2xs" py={1} isNumeric>Qtd</Th>
                                          <Th fontSize="2xs" py={1} isNumeric>Preço Original</Th>
                                          <Th fontSize="2xs" py={1} isNumeric>Preço Promo</Th>
                                          <Th fontSize="2xs" py={1} isNumeric>Desconto</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {itensPromo.map((item, idx) => {
                                          const precoOriginal = item.preco_original || item.valor_unitario;
                                          const precoPromo = item.valor_unitario;
                                          const descItem = precoOriginal > 0 ? ((precoOriginal - precoPromo) / precoOriginal) * 100 : 0;
                                          const produto = item.produto_estoque?.produto;
                                          const estoque = item.produto_estoque;

                                          return (
                                            <Tr key={idx}>
                                              <Td py={1}>
                                                <Text fontSize="xs" fontWeight="medium" isTruncated maxW="250px">
                                                  {produto?.nome || `Produto #${item.produto_estoque_id}`}
                                                </Text>
                                              </Td>
                                              <Td py={1} fontSize="2xs" color="gray.600">{estoque?.produto_id || "-"}</Td>
                                              <Td py={1} fontSize="2xs" color="gray.600">{produto?.codigo_global || "-"}</Td>
                                              <Td py={1} fontSize="2xs" color="gray.600">{estoque?.codigo_fornecedor || "-"}</Td>
                                              <Td py={1} isNumeric fontSize="xs">{item.quantidade}</Td>
                                              <Td py={1} isNumeric fontSize="xs" color="gray.500" textDecoration="line-through">
                                                R$ {parseFloat(precoOriginal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                              </Td>
                                              <Td py={1} isNumeric fontSize="xs" fontWeight="bold" color="green.600">
                                                R$ {parseFloat(precoPromo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                              </Td>
                                              <Td py={1} isNumeric>
                                                <Badge colorScheme="orange" fontSize="2xs">
                                                  {descItem > 0 ? `-${descItem.toFixed(1)}%` : "0%"}
                                                </Badge>
                                              </Td>
                                            </Tr>
                                          );
                                        })}
                                      </Tbody>
                                    </Table>
                                  </Box>
                                </Td>
                              </Tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>

                {/* Paginação das vendas */}
                <Flex justify="space-between" align="center" mt={4}>
                  <Text fontSize="sm" color="gray.600">
                    Página {vendasPagination.current_page} de {vendasPagination.last_page}
                  </Text>
                  <Flex gap={2}>
                    <Button
                      size="sm"
                      leftIcon={<ChevronLeftIcon />}
                      onClick={() => fetchVendas(vendasPagination.current_page - 1)}
                      isDisabled={vendasPagination.current_page <= 1}
                    >
                      Anterior
                    </Button>
                    <Button
                      size="sm"
                      rightIcon={<ChevronRightIcon />}
                      onClick={() => fetchVendas(vendasPagination.current_page + 1)}
                      isDisabled={vendasPagination.current_page >= vendasPagination.last_page}
                    >
                      Próxima
                    </Button>
                  </Flex>
                </Flex>
              </>
            )}
          </Box>
        )}

        {/* Itens da Promoção */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" mt={6}>
          <Flex justify="space-between" align="start" mb={4} flexWrap="wrap" gap={3}>
            <Box>
              <Heading size="md" color="gray.700" mb={3}>Itens da Promoção</Heading>
              <Tabs
                index={['todos','synced','error','sem_anuncio','pendente'].indexOf(syncFilter)}
                onChange={(i) => setSyncFilter(['todos','synced','error','sem_anuncio','pendente'][i])}
                size="sm"
                variant="soft-rounded"
                colorScheme="blue"
              >
                <TabList flexWrap="wrap" gap={1}>
                  <Tab fontSize="xs">Todos</Tab>
                  <Tab fontSize="xs" _selected={{ bg: 'green.100', color: 'green.700' }}>✓ Sincronizados</Tab>
                  <Tab fontSize="xs" _selected={{ bg: 'red.100', color: 'red.700' }}>✗ Com Erro</Tab>
                  <Tab fontSize="xs" _selected={{ bg: 'gray.200', color: 'gray.700' }}>Sem Anúncio</Tab>
                  <Tab fontSize="xs" _selected={{ bg: 'blue.100', color: 'blue.700' }}>Pendentes</Tab>
                </TabList>
              </Tabs>
            </Box>
            <InputGroup maxW="320px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por nome, SKU, código..."
                value={buscaItens}
                onChange={(e) => setBuscaItens(e.target.value)}
                bg="gray.50"
                size="sm"
              />
            </InputGroup>
          </Flex>

          {itensLoading ? (
            <Flex justify="center" py={10}><Spinner size="lg" color="orange.400" /></Flex>
          ) : itens.length === 0 ? (
            <Text textAlign="center" py={10} color="gray.500">Nenhum item encontrado.</Text>
          ) : (
            <>
              {isMobile ? renderCards() : renderTable()}
              {renderPagination()}
            </>
          )}
        </Box>

        {renderSyncModal()}
      </Container>
    </Box>
  );
};

export default DetalheListaPromocao;
